import { Link, useNavigate } from "react-router-dom";
import { Bell, ChevronDown, LogOut, Menu, Plus, Search, Settings, UserCircle, X } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

/* Design System DGA — topbar globale : recherche, création, notifications,
   paramètres, profil agence. Tous les comportements existants sont conservés. */
export default function AppHeader({ brand, user, unread, onSearch, onOpenMobile, mobileOpen, onLogout, onNewPub, onNewClient }) {
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-20 bg-[#FAF9F7]/85 backdrop-blur border-b border-[#E7E4DE] px-4 lg:px-8 h-16 flex items-center gap-3" data-testid="app-header">
      <button className="lg:hidden text-[#475569] hover:text-[#0F172A] transition-colors" onClick={onOpenMobile} data-testid="mobile-menu-btn">
        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Recherche globale — moteur existant (CommandBar) conservé */}
      <button data-testid="search-trigger" onClick={onSearch}
        className="flex items-center gap-2.5 text-sm text-[#94A3B8] bg-white border border-[#E7E4DE] hover:border-[#C9C4BA] rounded-xl px-4 py-2 transition-colors w-full max-w-md shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
        <Search className="w-4 h-4 shrink-0" />
        <span className="hidden sm:inline truncate">Rechercher un client, un projet, une demande...</span>
        <kbd className="hidden lg:inline ml-auto text-[10px] bg-[#FAF9F7] border border-[#E7E4DE] rounded px-1.5 py-0.5">⌘K</kbd>
      </button>

      <div className="flex-1" />

      {/* Création rapide (existant) */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button data-testid="create-menu-btn"
            className="bg-[#0B132B] hover:bg-[#1C2A4F] text-white rounded-xl gap-1.5 h-9 px-3.5">
            <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Créer</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem data-testid="create-publication" onClick={onNewPub}>Publication</DropdownMenuItem>
          <DropdownMenuItem data-testid="create-client" onClick={onNewClient}>Client</DropdownMenuItem>
          <DropdownMenuItem data-testid="create-task" onClick={() => navigate("/taches?new=1")}>Tâche</DropdownMenuItem>
          <DropdownMenuItem data-testid="create-idea" onClick={() => navigate("/idees")}>Idée</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Notifications (existant) */}
      <Link to="/notifications"
        className="relative w-9 h-9 rounded-xl bg-white border border-[#E7E4DE] flex items-center justify-center text-[#475569] hover:text-[#0F172A] hover:border-[#C9C4BA] transition-colors"
        data-testid="header-bell" title="Notifications">
        <Bell className="w-[18px] h-[18px]" strokeWidth={1.8} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-0.5 bg-[#BE123C] text-white text-[9px] font-bold rounded-full flex items-center justify-center"
            data-testid="nav-notif-count">{unread}</span>
        )}
      </Link>

      {/* Paramètres */}
      <Link to="/parametres"
        className="hidden sm:flex w-9 h-9 rounded-xl bg-white border border-[#E7E4DE] items-center justify-center text-[#475569] hover:text-[#0F172A] hover:border-[#C9C4BA] transition-colors"
        data-testid="header-settings" title="Paramètres">
        <Settings className="w-[18px] h-[18px]" strokeWidth={1.8} />
      </Link>

      {/* Profil agence */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2.5 rounded-xl pl-1.5 pr-2 py-1.5 hover:bg-white border border-transparent hover:border-[#E7E4DE] transition-colors" data-testid="profile-menu">
            <span className="w-8 h-8 rounded-full bg-[#0B132B] text-white flex items-center justify-center text-sm font-semibold overflow-hidden">
              {brand.app_logo_url
                ? <img src={brand.app_logo_url} alt="" className="w-full h-full object-cover" />
                : user?.name?.[0]?.toUpperCase()}
            </span>
            <span className="hidden md:block text-left leading-tight">
              <span className="block text-sm font-semibold text-[#0F172A]">{brand.app_name} Agence</span>
              <span className="block text-[11px] text-[#94A3B8] truncate max-w-[140px]">{user?.name}</span>
            </span>
            <ChevronDown className="w-3.5 h-3.5 text-[#94A3B8] hidden md:block" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuItem data-testid="profile-link" onClick={() => navigate("/profil")}>
            <UserCircle className="w-4 h-4 mr-2" /> Mon profil
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate("/parametres")} data-testid="profile-settings">
            <Settings className="w-4 h-4 mr-2" /> Paramètres
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="logout-btn" onClick={onLogout} className="text-[#BE123C] focus:text-[#BE123C]">
            <LogOut className="w-4 h-4 mr-2" /> Se déconnecter
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
